import React, { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/styles/ui/input-otp";
import { Separator } from "@/styles/ui/separator";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { debounce } from "lodash";
import { existsModal, pushModal } from "@/stores/modal";
import { popModal } from "@/stores/modal";
import { VerificationLoadingModal } from "@/components/modals/login/verification-loading-modal";
import { VerificationExpiredModal } from "@/components/modals/login/verification-expired-modal";
import { InfoIcon } from "lucide-react";
import { ContactButton } from "@/components/common/contact-button";
import { getAuthErrorMessage } from "@/services/auth-service";
import { VerificationErrorModal } from "@/components/modals/login/verification-error-modal";

interface VerificationCodeProps {
  onCodeComplete?: (
    code: string,
  ) => Promise<{ success: boolean; message?: string }>;
  error?: string;
}
const VERIFICATION_MODAL_ID = "verification-modal";
const VERIFICATION_EXPIRED_MODAL_ID = "verification-expired-modal";
const COUNTDOWN_TIME = 180;

export const VerificationCode: React.FC<VerificationCodeProps> = ({
  onCodeComplete,
}) => {
  const [value, setValue] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isExpired = countdown <= 0;
  const isWarning = countdown < 60 && !isExpired;
  const otpRef = useRef<HTMLDivElement>(null); // OTP 컴포넌트에 대한 ref

  useEffect(() => {
    if (isExpired) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [isExpired]);

  useEffect(() => {
    if (!isExpired || existsModal(VERIFICATION_EXPIRED_MODAL_ID)) return;

    pushModal(VerificationExpiredModal, {
      id: VERIFICATION_EXPIRED_MODAL_ID,
      closeOnPointerDownOutside: false,
      closeOnEscapeKey: false,
    });
  }, [isExpired]);

  const debouncedOnCodeComplete = useCallback(
    debounce(async (value: string) => {
      setIsLoading(true);
      setVerificationError("");

      try {
        // Call the verification function
        const result = await onCodeComplete?.(value);
        popModal(); // Close loading modal

        if (!result?.success) {
          // Failure handling
          setVerificationError(
            "알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
          );
          setIsLoading(false);

          setTimeout(() => {
            setValue("");
            setVerificationError("");

            const inputs = document.querySelectorAll(
              'input[name="verification-code"]',
            );
            const firstInput = inputs[0] as HTMLInputElement;
            firstInput?.focus();
          }, 2000);
        }
      } catch (error: any) {
        popModal();

        const permittedErrors = [
          "auth/multi-factor-auth-required",
          "auth/invalid-verification-code",
          "auth/session-expired",
          "auth/code-expired",
        ];
        if (!permittedErrors.includes(error.code)) {
          pushModal(VerificationErrorModal, {
            closeOnPointerDownOutside: false,
            closeOnEscapeKey: false,
          });
        }

        let errorMessage = getAuthErrorMessage(error);
        // let errorMessage =
        //   String(error.message) || "인증 코드가 올바르지 않습니다";
        if (error.message.includes("Unknown error occurred")) {
          errorMessage =
            "알 수 없는 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
        }

        setVerificationError(errorMessage);

        setIsLoading(false);

        setTimeout(() => {
          setValue("");
          setVerificationError("");

          const inputs = document.querySelectorAll(
            'input[name="verification-code"]',
          );
          const firstInput = inputs[0] as HTMLInputElement;
          firstInput?.focus();
        }, 2000);
      }
    }, 100),
    [onCodeComplete],
  );

  const handleChange = (newValue: string) => {
    if (isExpired) return;

    setValue(newValue);
    if (newValue.length === 6 && !existsModal(VERIFICATION_MODAL_ID)) {
      pushModal(VerificationLoadingModal, { id: VERIFICATION_MODAL_ID });
      debouncedOnCodeComplete(newValue);
      setTimeout(() => {
        // OTP 입력 필드들의 부모 요소 내의 모든 input을 찾아서 blur
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement.tagName === "INPUT") {
          activeElement.blur();
        }

        // 또는 더 안전하게 모든 OTP input을 blur
        const inputs = otpRef.current?.querySelectorAll("input");
        inputs?.forEach((input) => input.blur());
      }, 100); // 약간의 딜레이로 더 자연스럽게
    }
  };

  return (
    <div className="flex flex-col gap-6 items-center w-full max-w-[360px] mx-auto">
      <div className="flex flex-col gap-6">
        <div className="text-center space-y-3 ">
          <h1 className="title-2-bold text-primary">인증코드 입력 </h1>
          <p className="label-1-normal-medium text-[#737373] whitespace-pre-line">
            {`보안을 위해 SMS 인증을 진행합니다. \n 문자 메시지의 인증코드 6자리를 입력해주세요.`}
          </p>
          <div className="p-[10px] rounded-md bg-neutral-50">
            <span className="text-neutral-500 !label-1-normal-medium flex items-center gap-1">
              <InfoIcon className="w-4 h-4 text-neutral-500" />
              인증번호는{" "}
              <span className="!label-1-normal-medium text-primary">
                [국제발신]
              </span>{" "}
              문자를 통해 안전하게 발송됩니다.
            </span>
          </div>
        </div>

        {/* Code Input Grid */}
        <div className="flex flex-col gap-3 items-center w-full">
          <div className="flex flex-col gap-2 justify-center" ref={otpRef}>
            <p className="text-foreground label-1-normal-medium">인증 코드</p>
            <InputOTP
              className={verificationError ? "animate-shake" : ""}
              maxLength={6}
              value={value}
              onChange={handleChange}
              pattern={REGEXP_ONLY_DIGITS}
              autoFocus
              name="verification-code"
              disabled={isLoading || isExpired}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Timer or Error */}
          <div className="min-h-[20px] flex items-center justify-center">
            {verificationError ? (
              <p className="label-1-normal-medium text-[#DC2626] animate-in fade-in-50 slide-in-from-bottom-2 duration-200 whitespace-pre-line">
                {verificationError}
              </p>
            ) : (
              <p
                className={cn(
                  "label-1-normal-medium text-[#0A0A0A] transition-colors duration-300",
                  isWarning ? "text-[#DC2626]" : "text-neutral-500",
                )}
              >
                {formatTime(Math.max(countdown, 0))}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Separator className="w-[300px] h-[1px] bg-[#E5E5E5] my-[10px]" />

        {/* Actions */}
        <div className="flex flex-col gap-3 items-center w-full ">
          <p className="label-1-normal-medium text-foreground text-center">
            <span>코드가 도착하지 않나요? </span>
            <ContactButton className="!text-primary" />
          </p>
        </div>
      </div>
    </div>
  );
};
